import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Cpu, 
  Terminal, 
  Target, 
  Sword,
  Shield,
  Flame,
  Crosshair,
  Check,
  AlertCircle,
  X,
  Clock,
  Zap,
  Coins
} from 'lucide-react';
import { cn } from '../lib/utils';

interface Strategy {
  id: string;
  name: string;
  description: string;
  icon: any;
  color: string;
}

const STRATEGIES: Strategy[] = [
  {
    id: 'aggressive',
    name: 'Aggressive',
    description: 'High risk, high reward. Focus on early eliminations.',
    icon: Flame,
    color: 'text-red-500',
  },
  {
    id: 'balanced',
    name: 'Balanced',
    description: 'Adaptive tactics. Flexible positioning and engagement.',
    icon: Target,
    color: 'text-gold',
  },
  {
    id: 'defensive',
    name: 'Defensive',
    description: 'Survival focused. Wait for opponents to make mistakes.',
    icon: Shield,
    color: 'text-blue-500',
  },
];

interface StrategyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (strategy: Strategy | null, customStrategy: string, autoRegister: boolean) => void;
  roundNumber: number;
  creditCost: number;
  creditBalance: number;
  autoRegister?: boolean;
}

export default function StrategyModal({ 
  isOpen, 
  onClose, 
  onConfirm,
  roundNumber,
  creditCost,
  creditBalance,
  autoRegister: initialAutoRegister = true
}: StrategyModalProps) {
  const [selectedStrategy, setSelectedStrategy] = useState<Strategy | null>(null);
  const [customStrategy, setCustomStrategy] = useState('');
  const [countdown, setCountdown] = useState(45);
  const [showConfirm, setShowConfirm] = useState(false);
  const [autoRegister, setAutoRegister] = useState(initialAutoRegister);
  
  useEffect(() => {
    if (isOpen) {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 0) return 0;
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedStrategy(null);
      setCustomStrategy('');
      setShowConfirm(false);
      setCountdown(45);
    }
  }, [isOpen]);

  const handleDeploy = () => {
    setShowConfirm(true);
  };

  const confirmDeploy = () => {
    onConfirm(selectedStrategy, customStrategy, autoRegister);
    setShowConfirm(false);
    onClose();
  };

  const hasEnoughCredits = creditBalance >= creditCost;
  const canDeploy = (selectedStrategy || customStrategy) && hasEnoughCredits;
  const monCost = creditCost / 10;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-bg/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-surface border border-line rounded-2xl w-full max-w-lg overflow-hidden"
          >
            {/* Header */}
            <div className="p-5 border-b border-line flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center">
                  <Cpu size={20} className="text-gold" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Join Round {roundNumber}</h2>
                  <p className="text-xs text-muted">Select your battle strategy</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 rounded-full">
                  <Clock size={14} className="text-red-500" />
                  <span className={cn(
                    "text-sm font-mono font-bold",
                    countdown <= 10 ? "text-red-500" : "text-red-400"
                  )}>
                    {countdown}s
                  </span>
                </div>
                <button onClick={onClose} className="text-muted hover:text-ink">
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 space-y-5">
              {/* Credit Info */}
              <div className="p-4 bg-gold/5 border border-gold/20 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Coins size={16} className="text-gold" />
                    <span>Credit Cost</span>
                  </div>
                  <span className="font-mono font-bold text-gold">{creditCost} Credits</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted">Your Balance</span>
                  <span className={cn(
                    "font-mono font-bold",
                    hasEnoughCredits ? "text-ink" : "text-red-500"
                  )}>
                    {creditBalance} Credits
                  </span>
                </div>
                <p className="text-xs text-muted mt-2">
                  Credits power your AI during battle. ~{monCost} $MON equivalent.
                </p>
              </div>

              {/* Strategy Selection */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Choose Strategy</h3>
                <div className="space-y-2">
                  {STRATEGIES.map((strategy) => (
                    <button
                      key={strategy.id}
                      onClick={() => {
                        setSelectedStrategy(strategy);
                        setCustomStrategy('');
                      }}
                      className={cn(
                        "w-full p-4 border rounded-xl text-left transition-all flex items-center gap-3",
                        selectedStrategy?.id === strategy.id
                          ? "border-gold bg-gold/5"
                          : "border-line hover:border-gold/30"
                      )}
                    >
                      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center bg-bg", strategy.color)}>
                        <strategy.icon size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">{strategy.name}</div>
                        <div className="text-xs text-muted">{strategy.description}</div>
                      </div>
                      {selectedStrategy?.id === strategy.id && (
                        <div className="w-6 h-6 bg-gold rounded-full flex items-center justify-center">
                          <Check size={14} className="text-bg" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Strategy */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Or Custom Strategy</h3>
                <textarea
                  value={customStrategy}
                  onChange={(e) => {
                    setCustomStrategy(e.target.value);
                    if (e.target.value) setSelectedStrategy(null);
                  }}
                  placeholder="Describe your custom strategy..."
                  className="w-full h-20 bg-bg border border-line rounded-xl p-3 text-sm focus:outline-none focus:border-gold/50 resize-none"
                />
              </div>

              {/* Auto Register */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={autoRegister}
                  onChange={(e) => setAutoRegister(e.target.checked)}
                  className="w-4 h-4 text-gold rounded border-line"
                />
                <div>
                  <div className="text-sm font-medium">Auto join next rounds</div>
                  <div className="text-xs text-muted">Use same strategy automatically</div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-line">
              {!hasEnoughCredits && (
                <div className="flex items-center gap-2 text-red-500 text-sm mb-3">
                  <AlertCircle size={16} />
                  <span>Insufficient credits. Please buy more.</span>
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 py-3 border border-line rounded-xl text-sm font-medium text-muted hover:text-ink transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeploy}
                  disabled={!canDeploy}
                  className={cn(
                    "flex-1 py-3 rounded-xl text-sm font-semibold transition-colors",
                    canDeploy
                      ? "bg-gold text-bg hover:bg-white"
                      : "bg-muted text-muted cursor-not-allowed"
                  )}
                >
                  {hasEnoughCredits ? 'Join Battle' : 'Need Credits'}
                </button>
              </div>
            </div>
          </motion.div>

          {/* Confirm Dialog */}
          <AnimatePresence>
            {showConfirm && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-bg/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-surface border border-line rounded-2xl p-6 max-w-sm w-full"
                >
                  <div className="text-center space-y-4">
                    <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center mx-auto">
                      <Target size={24} className="text-gold" />
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-bold mb-1">Confirm Join</h3>
                      <p className="text-sm text-muted">
                        Deploy <span className="text-gold font-semibold">{selectedStrategy?.name || 'Custom'}</span> for Round {roundNumber}
                      </p>
                    </div>
                    
                    <div className="bg-bg rounded-xl p-4 space-y-2 text-left text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted">Strategy</span>
                        <span className="font-medium">{selectedStrategy?.name || 'Custom'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted">Credits</span>
                        <span className="font-mono text-gold">-{creditCost}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted">Remaining</span>
                        <span className="font-mono">{creditBalance - creditCost}</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowConfirm(false)}
                        className="flex-1 py-2.5 border border-line rounded-xl text-sm font-medium text-muted hover:text-ink"
                      >
                        Back
                      </button>
                      <button
                        onClick={confirmDeploy}
                        className="flex-1 py-2.5 bg-gold text-bg rounded-xl text-sm font-semibold hover:bg-white"
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
